import {
    makeStyles, Card, CardHeader, CardContent, Container, Divider, Grid, Typography, colors,
    Table, TableHead, TableBody, TableCell, TableRow, TableFooter, IconButton, Tooltip, Zoom, Link
} from '@material-ui/core';
import { GetApp as DownloadIcon } from '@material-ui/icons';
import { Page, Label, RegistrationStatus, DeleteIconButton, RestoreIconButton } from 'components';
import { useContext } from 'react';
import { registrationContext } from './RegistrationContext';
import Moment from 'react-moment';
import { DataLabel, DataValue, Editor } from './components';
import { appUserContext } from 'helpers/services';
import { UserType } from 'helpers/enums';


let useStyles = makeStyles((theme) => ({
    card: {
        marginTop: theme.spacing(2)
    }
}))

let RegistrationEdit = () => {
    let classes = useStyles();

    let { currentUser } = useContext(appUserContext);
    let service = useContext(registrationContext);


    return (
        <Container maxWidth="lg">
            <Page
                useHeader={true}
                title="Registration details"
                isLoading={service.isLoading}
            >
                {service.isLoading === false &&
                    <>
                        <Card>
                            <CardHeader
                                disableTypography
                                title={<Typography variant="h4">Warranty</Typography>}
                                action={currentUser?.userType !== UserType.External &&
                                    <>
                                        <Editor registration={service.registration} />
                                        {service.registration.isDeleted === false &&
                                            <DeleteIconButton size="medium" onClick={service.deleteRegistration} />
                                        }
                                        {service.registration.isDeleted === true &&
                                            <RestoreIconButton size="medium" onClick={service.restoreRegistration} />
                                        }
                                    </>
                                }
                            />
                            <Divider />
                            <CardContent>
                                <Grid container>
                                    <Grid item xs>
                                        {service.registration.unlimited &&
                                            <Label shape="square" variant="contained" color={colors.green[600]}>Unlimited warranty</Label>
                                        }
                                        {service.registration.unlimited === false &&
                                            <>
                                                {service.registration.hasWarrantyExtension &&
                                                    <DataValue>{service.registration.extensionMonths}&nbsp;months</DataValue>
                                                }
                                                <DataLabel>
                                                    <RegistrationStatus
                                                        isExtended={service.registration.hasWarrantyExtension}
                                                        isPreGolive={service.registration.isPreGolive}
                                                    />
                                                </DataLabel>
                                            </>
                                        }
                                    </Grid>
                                    <Grid item xs>
                                        <DataValue>
                                            <Moment date={service.registration.purchaseDate} format="LL" />
                                        </DataValue>
                                        <DataLabel>Purchase Date</DataLabel>
                                    </Grid>
                                    <Grid item xs>
                                        <DataValue>
                                            <Moment date={service.registration.registrationDate} format="LL" />
                                        </DataValue>
                                        <DataLabel>Registration Date</DataLabel>
                                    </Grid>
                                    <Grid item xs>
                                        <DataValue>
                                            <Moment date={service.registration.warrantyExpiration} format="LL" />
                                        </DataValue>
                                        <DataLabel>Expiration Date</DataLabel>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        <Card className={classes.card}>
                            <CardHeader
                                disableTypography
                                title={<Typography variant="h4">Purchase Informations</Typography>}
                                action={
                                    <>
                                        {service.registration?.receiptDownloadUrl &&
                                            <Tooltip TransitionComponent={Zoom} title="Download receipt">
                                                <IconButton component={Link} href={service.registration.receiptDownloadUrl}>
                                                    <DownloadIcon />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    </>
                                }
                            />
                            <Divider />
                            <CardContent>
                                <Grid container>
                                    <Grid item xs>
                                        <DataValue>{service.registration.sku}</DataValue>
                                        <DataLabel>SKU - Part number</DataLabel>
                                    </Grid>
                                    <Grid item xs>
                                        <DataValue>{service.registration.serialNumber || 'n/a'}</DataValue>
                                        <DataLabel>Serial number</DataLabel>
                                    </Grid>
                                    <Grid item xs>
                                        <DataValue>{service.registration.dealerName}</DataValue>
                                        <DataLabel>Dealer</DataLabel>
                                    </Grid>
                                    <Grid item xs>
                                        <DataValue>{service.registration.dealerCountryName}</DataValue>
                                        <DataLabel>Dealer country</DataLabel>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        <Card className={classes.card}>
                            <CardHeader
                                disableTypography
                                title={<Typography variant="h4">QrCode Informations</Typography>}
                            />
                            <Divider />
                            <CardContent>
                                <Grid container>
                                    <Grid item xs>
                                        <DataValue>{service.registration.isQrCodeMandatory ? "Yes" : "No"}</DataValue>
                                        <DataLabel>QrCode mandatory</DataLabel>
                                    </Grid>
                                    <Grid item xs>
                                        <DataValue>{service.registration.isQrCodePresent ? "Yes" : "No"}</DataValue>
                                        <DataLabel>QrCode present</DataLabel>
                                    </Grid>
                                    <Grid item xs>
                                        <DataValue>{service.registration.qrCode || 'n/a'}</DataValue>
                                        <DataLabel>QrCode</DataLabel>
                                    </Grid>
                                    <Grid item xs>
                                        <DataValue>{service.registration.qrDealerCountryCode}</DataValue>
                                        <DataLabel>Dealer Country provided by QrCode</DataLabel>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        <Card className={classes.card}>
                            <CardHeader
                                disableTypography
                                title={<Typography variant="h4">User Informations</Typography>}
                            />
                            <Divider />
                            <CardContent>
                                <Grid container>
                                    <Grid item xs>
                                        <DataValue>{service.registration.firstName || '-'}</DataValue>
                                        <DataLabel>First Name</DataLabel>
                                    </Grid>
                                    <Grid item xs>
                                        <DataValue>{service.registration.lastName || '-'}</DataValue>
                                        <DataLabel>Last Name</DataLabel>
                                    </Grid>
                                    <Grid item xs>
                                        <DataValue>{service.registration.email}</DataValue>
                                        <DataLabel>Email</DataLabel>
                                    </Grid>
                                    <Grid item xs>
                                        <DataValue>{service.registration.userCountry}</DataValue>
                                        <DataLabel>Country</DataLabel>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {currentUser?.userType !== UserType.External &&
                            <>
                                <Card className={classes.card}>
                                    <CardHeader
                                        disableTypography
                                        title={<Typography variant="h4">Update operations</Typography>}
                                    />
                                    <Divider />
                                    <CardContent>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>User</TableCell>
                                                    <TableCell>Description</TableCell>
                                                    <TableCell>Reason</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {service.registration?.log
                                                    .map((log, index) =>
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <Moment date={log.logDate} format="LLL" />
                                                            </TableCell>
                                                            <TableCell>{log.userName}</TableCell>
                                                            <TableCell>{log.log}</TableCell>
                                                            <TableCell>{log.reason}</TableCell>
                                                        </TableRow>
                                                    )}
                                            </TableBody>
                                            {service.registration?.log.length === 0 &&
                                                <TableFooter>
                                                    <TableRow>
                                                        <TableCell colSpan={4}>
                                                            <i>No logs were found</i>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableFooter>
                                            }
                                        </Table>
                                    </CardContent>
                                </Card>

                                <Card className={classes.card}>
                                    <CardHeader
                                        disableTypography
                                        title={<Typography variant="h4">Linked claims</Typography>}
                                    />
                                    <Divider />
                                    <CardContent>
                                        {service.registration.linkedClaims.length !== 0 &&
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Claim number</TableCell>
                                                        <TableCell>Claim created on</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {service.registration.linkedClaims
                                                        .map((claim, index) =>
                                                            <TableRow key={index}>
                                                                <TableCell>
                                                                    <Link target='_blank' href={`${process.env.REACT_APP_CLAIMS_URL}/ticket/${claim.ticketNuber}`}>
                                                                        {claim.ticketNuber}
                                                                    </Link>
                                                                </TableCell>

                                                                <TableCell>
                                                                    <Moment date={claim.ticketDate} format="LL" />
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                </TableBody>
                                            </Table>}

                                        {service.registration.linkedClaims.length === 0 && <Typography>No claims linked to this product registration</Typography>}
                                    </CardContent>
                                </Card>
                            </>
                        }
                    </>
                }
            </Page>
        </Container>
    )
}

export default RegistrationEdit;